import React, { useEffect, useMemo, useRef, useState } from 'react';

import { Button, Image as Img, Modal, Row, Space, message } from 'antd';
import { Carousel, Progress, Spin } from 'antd';
import { CarouselRef } from 'antd/es/carousel';

import { CloseOutlined } from '@ant-design/icons';
import RenderIf from '@common/RenderIf';
import { convertToJpeg } from '@helpers/convertToJpeg';

import { GlobalConstants } from '../../../constants/global';
import { ImgCropper } from './components';
import { ImageSlide } from './components/imgSlide';
import { VideoSlide } from './components/videoSlide';

import './imgUploader.scss';

const isAllowImage = (width: number, height: number) => {
    return (
        width >= GlobalConstants.UploadImg.minHeight &&
        width <= GlobalConstants.UploadImg.maxHeight &&
        height >= GlobalConstants.UploadImg.minHeight &&
        height <= GlobalConstants.UploadImg.maxHeight
    );
};

const convertBase64ToBlob = (base64Image: string) => {
    const parts = base64Image.split(';base64,');
    const imageType = parts[0].split(':')[1];
    const decodedData = window.atob(parts[1]);
    const uInt8Array = new Uint8Array(decodedData.length);
    for (let i = 0; i < decodedData.length; ++i) {
        uInt8Array[i] = decodedData.charCodeAt(i);
    }
    return new Blob([uInt8Array], { type: imageType });
};

const isAllowedFile = (file: File) => {
    const allowedImages = ['image/jpeg', 'image/png'];
    const allowedVideos = ['video/mp4', 'video/webm', 'video/ogg'];
    return allowedImages.includes(file.type) || allowedVideos.includes(file.type);
};

type Props = {
    picture?: string;
    pictures?: string[];
    isEdit: boolean;
    onImageSelect?: (file: File) => Promise<void>;
    onImageDelete?: (index: number) => Promise<void>;
    imgWidth?: number | string;
    imgHeight?: number | string;
    imgStyle?: React.CSSProperties;
    isAvatar?: boolean;
    isCRM?: boolean;
    progress?: { percentage: number; file: File } | null;
};

const ImgUploader = ({
    picture,
    pictures,
    onImageSelect,
    isEdit,
    onImageDelete,
    imgWidth,
    imgHeight = 160,
    imgStyle = {},
    isAvatar = true,
    isCRM = false,
    progress,
}: Props) => {
    const [isLoading, setIsLoading] = useState(false);
    const [isDeleting, setIsDeleting] = useState(false);
    const [isModalOpen, setModalOpen] = useState(false);
    const [img, setImg] = useState<string>('');
    const cropImg = useRef('#');
    const carouselRef = useRef<CarouselRef>(null);
    const [fileName, setFileName] = useState('');
    const [imgIndex, setImgIndex] = useState(0);
    const [percentage, setPercentage] = useState<number | null>(null);

    let normalizedPictures = useMemo(() => [...(pictures || [])], [pictures]);
    if (!normalizedPictures?.length && picture) {
        normalizedPictures = [picture];
    }
    const isMany = Boolean(normalizedPictures?.length > 1);

    const handleCancel = () => setModalOpen(false);

    const handleOk = () => {
        setPercentage(0);
        const blob = convertBase64ToBlob(cropImg.current);
        const file = new File([blob], fileName);
        setIsLoading(true);
        Promise.resolve(onImageSelect(file)).finally(() => setIsLoading(false));
        setModalOpen(false);
    };

    const handleFileSelect = async (e: React.ChangeEvent<HTMLInputElement>) => {
        e.preventDefault();
        setIsLoading(true);
        const input = e.target;
        let file = input.files[0];
        if (!file) return setIsLoading(false);

        const fileName = file.name;

        if (fileName.includes('.heic')) {
            const newName = fileName.replace(/\.heic$/, '.jpg');
            const convertFile = await convertToJpeg(file);
            if (!Array.isArray(convertFile)) {
                file = new File([convertFile], newName, {
                    lastModified: +new Date().toISOString(),
                    type: 'image/jpeg',
                });
            }
        }

        if (!isAllowedFile(file)) {
            message.error('Пожалуйста, загрузите изображение или видео в поддерживаемом формате!');
            setIsLoading(false);
            return false;
        }

        if (file.size >= GlobalConstants.UploadImg.maxSize) {
            message.error('Файл должен весить меньше 10MB');
            setIsLoading(false);
            return false;
        }

        if (file.type.startsWith('image/')) {
            const reader = new FileReader();
            reader.onload = () => {
                const imgUrl = reader.result.toString();
                const imgEl = new Image();
                imgEl.src = imgUrl;
                imgEl.onload = () => {
                    input.value = null;
                    if (!isAllowImage(imgEl.width, imgEl.height)) {
                        message.error('Разрешение картинки должно быть от 200х200 до 5120х5120');
                        setIsLoading(false);
                        return;
                    }
                    setImg(imgUrl);
                    setModalOpen(true);
                    setFileName(file.name);
                    setIsLoading(false);
                };
            };
            reader.readAsDataURL(file);
        } else if (file.type.startsWith('video/')) {
            const videoUrl = URL.createObjectURL(file);
            setImg(videoUrl);
            setFileName(file.name);
            setIsLoading(false);
            Promise.resolve(onImageSelect(file)).finally(() => setIsLoading(false));
        }
    };

    const handleOnCrop = (img) => (cropImg.current = img);

    useEffect(() => {
        if (!isEdit) setFileName('');
    }, [isEdit]);

    useEffect(() => {
        let timeout;
        if (progress?.percentage) {
            if (progress.percentage === 100) {
                timeout = setTimeout(() => setPercentage(null), 500);
            }
            setPercentage(progress.percentage);
        }
        return () => clearTimeout(timeout);
    }, [progress?.percentage]);

    useEffect(() => {
        if (carouselRef.current) {
            carouselRef.current.goTo(isEdit ? normalizedPictures.length - 1 : 0, false);
        }
    }, [isEdit, JSON.stringify(normalizedPictures)]);

    const handleDeleteImage = (index: number) => {
        Modal.confirm({
            title: 'Удалить изображение?',
            okText: 'Удалить',
            okType: 'danger',
            cancelText: 'Отмена',
            centered: true,
            onOk() {
                setIsDeleting(true);
                Promise.resolve(onImageDelete(index)).finally(() => setIsDeleting(false));
            },
        });
    };

    return (
        <Row justify="center" style={{ width: '100%' }}>
            <Space direction="vertical" className="uploader__buttons" style={{ width: '100%' }}>
                <Img.PreviewGroup>
                    <Carousel
                        ref={carouselRef}
                        className="custom-carousel"
                        dots={isMany}
                        arrows={isMany}
                        afterChange={(current) => setImgIndex(current)}
                        infinite={false}
                    >
                        {normalizedPictures.map((pic, index) => {
                            const isBlob = pic?.startsWith('blob:');
                            const isVideo =
                                pic?.match(/\.(mp4|webm|ogg)$/i) || pic?.startsWith('blob:video');

                            return (
                                <div key={index}>
                                    <div style={{ position: 'relative', textAlign: 'center' }}>
                                        {isVideo ? (
                                            <VideoSlide
                                                src={pic}
                                                height={imgHeight}
                                                width={imgWidth}
                                                loop
                                            />
                                        ) : (
                                            <ImageSlide
                                                src={pic}
                                                height={imgHeight}
                                                width={imgWidth}
                                                style={imgStyle}
                                            />
                                        )}

                                        <RenderIf condition={percentage !== null && isBlob}>
                                            <div
                                                style={{
                                                    position: 'absolute',
                                                    top: 0,
                                                    left: 0,
                                                    backdropFilter: 'blur(2px)',
                                                    width: '100%',
                                                    height: '100%',
                                                    backgroundColor: 'rgba(255, 255, 255, 0.4)',
                                                }}
                                            >
                                                <Progress
                                                    style={{
                                                        position: 'absolute',
                                                        top: '50%',
                                                        left: '50%',
                                                        transform: 'translate(-50%, -50%)',
                                                    }}
                                                    type="circle"
                                                    percent={percentage || 0}
                                                    trailColor="grey"
                                                />
                                            </div>
                                        </RenderIf>
                                    </div>
                                </div>
                            );
                        })}
                    </Carousel>
                </Img.PreviewGroup>
                {!isEdit || (
                    <>
                        {pictures?.length > 0 && (
                            <Button
                                type="primary"
                                danger
                                shape="circle"
                                onClick={() => handleDeleteImage(imgIndex)}
                                disabled={isDeleting}
                                style={{ position: 'absolute', top: 5, right: 5, zIndex: 11 }}
                                icon={isDeleting ? <Spin /> : <CloseOutlined />}
                            />
                        )}
                        <Row align="stretch" className="uploader__input-wrapper">
                            <Button
                                block
                                type="primary"
                                className="uploader__btn"
                                disabled={isLoading}
                            >
                                {isLoading ? <Spin /> : 'Добавить фото/видео'}
                            </Button>
                            <input
                                className="uploader__input"
                                type="file"
                                onChange={handleFileSelect}
                                multiple={false}
                            />
                        </Row>
                    </>
                )}
            </Space>

            <Modal
                title="Выберите область"
                cancelText="Отменить"
                open={isModalOpen}
                onOk={handleOk}
                onCancel={handleCancel}
                centered
            >
                <ImgCropper
                    img={img}
                    handleOnCrop={handleOnCrop}
                    isAvatar={isAvatar}
                    isCRM={isCRM}
                />
            </Modal>
        </Row>
    );
};

export { ImgUploader, isAllowImage };
